import { ATTENDANCE_URL } from "../constants";
import { apiSlice } from "./apiSlice";

export const calendarApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Get all calendars
    getCalendars: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.academy_id)
          queryParams.append("academy_id", params.academy_id);
        if (params.year) queryParams.append("year", params.year);

        const queryString = queryParams.toString();
        return {
          url: `${ATTENDANCE_URL}/calendars${queryString ? `?${queryString}` : ""}`,
          method: "GET",
        };
      },
      transformResponse: (response) => {
        // Handle both array response and object with calendars property
        if (Array.isArray(response)) {
          return response;
        }
        if (response && Array.isArray(response.calendars)) {
          return response.calendars;
        }
        // Fallback to empty array if unexpected format
        return [];
      },
      providesTags: ["Calendars"],
    }),

    // Get calendar by ID
    getCalendarById: builder.query({
      query: (id) => ({
        url: `${ATTENDANCE_URL}/calendars/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Calendar", id }],
    }),

    // Get calendar by academy and year
    getCalendarByAcademyYear: builder.query({
      query: ({ academy_id, year }) => ({
        url: `${ATTENDANCE_URL}/calendars/by-academy-year?academy_id=${academy_id}&year=${year}`,
        method: "GET",
      }),
      transformResponse: (response) => {
        // Handle both direct object and wrapped responses
        if (!response) return null;
        if (response.calendar) return response;
        // If response is the calendar object directly, wrap it
        if (response._id || response.id) return { calendar: response };
        return response;
      },
      providesTags: (result, error, { academy_id, year }) => [
        { type: "Calendar", id: `${academy_id}-${year}` },
      ],
    }),

    // Create new calendar
    createCalendar: builder.mutation({
      query: (calendarData) => ({
        url: ATTENDANCE_URL + "/calendars",
        method: "POST",
        body: calendarData,
      }),
      invalidatesTags: ["Calendars"],
    }),

    // Update calendar
    updateCalendar: builder.mutation({
      query: ({ id, ...calendarData }) => ({
        url: `${ATTENDANCE_URL}/calendars/${id}`,
        method: "PUT",
        body: calendarData,
      }),
      invalidatesTags: (result, error, { id }) => [
        "Calendars",
        { type: "Calendar", id },
      ],
    }),

    // Delete calendar
    deleteCalendar: builder.mutation({
      query: (id) => ({
        url: `${ATTENDANCE_URL}/calendars/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        "Calendars",
        { type: "Calendar", id },
      ],
    }),

    // Update calendar metadata (PATCH)
    updateCalendarMetadata: builder.mutation({
      query: ({ id, ...metadata }) => ({
        url: `${ATTENDANCE_URL}/calendars/${id}/metadata`,
        method: "PATCH",
        body: metadata,
      }),
      invalidatesTags: (result, error, { id }) => [
        "Calendars",
        { type: "Calendar", id },
      ],
    }),

    // Holiday management
    addHoliday: builder.mutation({
      query: ({ calendarId, holidayData }) => ({
        url: `${ATTENDANCE_URL}/calendars/${calendarId}/holidays`,
        method: "POST",
        body: holidayData,
      }),
      invalidatesTags: (result, error, { calendarId }) => [
        { type: "Calendar", id: calendarId },
        "Calendars",
      ],
    }),

    updateHoliday: builder.mutation({
      query: ({ calendarId, holidayId, holidayData }) => ({
        url: `${ATTENDANCE_URL}/calendars/${calendarId}/holidays/${holidayId}`,
        method: "PUT",
        body: holidayData,
      }),
      invalidatesTags: (result, error, { calendarId }) => [
        { type: "Calendar", id: calendarId },
        "Calendars",
      ],
    }),

    deleteHoliday: builder.mutation({
      query: ({ calendarId, holidayId }) => ({
        url: `${ATTENDANCE_URL}/calendars/${calendarId}/holidays/${holidayId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { calendarId }) => [
        { type: "Calendar", id: calendarId },
        "Calendars",
      ],
    }),

    // Leave balance management
    addLeaveBalance: builder.mutation({
      query: ({ calendarId, leaveBalanceData }) => ({
        url: `${ATTENDANCE_URL}/calendars/${calendarId}/leave_balances`,
        method: "POST",
        body: leaveBalanceData,
      }),
      invalidatesTags: (result, error, { calendarId }) => [
        { type: "Calendar", id: calendarId },
        "Calendars",
      ],
    }),

    updateLeaveBalance: builder.mutation({
      query: ({ calendarId, leaveBalanceId, leaveBalanceData }) => ({
        url: `${ATTENDANCE_URL}/calendars/${calendarId}/leave_balances/${leaveBalanceId}`,
        method: "PUT",
        body: leaveBalanceData,
      }),
      invalidatesTags: (result, error, { calendarId }) => [
        { type: "Calendar", id: calendarId },
        "Calendars",
      ],
    }),

    deleteLeaveBalance: builder.mutation({
      query: ({ calendarId, leaveBalanceId }) => ({
        url: `${ATTENDANCE_URL}/calendars/${calendarId}/leave_balances/${leaveBalanceId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { calendarId }) => [
        { type: "Calendar", id: calendarId },
        "Calendars",
      ],
    }),
    checkCalendarExists: builder.query({
      query: ({ academy_id, year }) => ({
        url: `${ATTENDANCE_URL}/calendars/check?academy_id=${academy_id}&year=${year}`,
        method: "GET",
      }),
      transformResponse: (response) => {
        return response?.exists || false;
      },
    }),
  }),
});

export const {
  useGetCalendarsQuery,
  useGetCalendarByIdQuery,
  useGetCalendarByAcademyYearQuery,
  useCreateCalendarMutation,
  useUpdateCalendarMutation,
  useDeleteCalendarMutation,
  useUpdateCalendarMetadataMutation,
  useAddHolidayMutation,
  useUpdateHolidayMutation,
  useDeleteHolidayMutation,
  useAddLeaveBalanceMutation,
  useUpdateLeaveBalanceMutation,
  useDeleteLeaveBalanceMutation,
  useCheckCalendarExistsQuery,
} = calendarApiSlice;
